import React from "react";
import { useNavigate } from "react-router";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DashboardCard from '../../common/DashboardCard';
import { Box, useTheme } from '@mui/material';
import PieChartIcon from '@mui/icons-material/PieChart';

interface InputDataItem {
  name: string;
  percentage: number;
}

interface ChartDataItem {
  name: string;
  value: number;
}

interface ChargingSummaryPieChartProps {
  total: string;
  unit: string;
  fleetScenarioUuid: string;
  fleetScenarioNumber: string;
  title: string;
  height: number;
  width: number;
  data: InputDataItem[];
  accentColor?: 'primary' | 'secondary' | 'success' | 'info' | 'warning';
}

const getIntroOfPage = (label: string) => {
  if (label === "Single-Charge") {
    return "Single charge tells the amount of journey that required only one single charge";
  }
  if (label === "Multiple-charge") {
    return "Multiple charge tells the amount of journey that required more than one charge";
  }
  if (label === "Non-Charge") {
    return "Non charge tells the amount of journey that required no charge";
  }
  return "";
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          fontSize: 12,
          color: "black",
          backgroundColor: "rgba(255,255,255,0.9)",
          borderRadius: 8,
          padding: 12,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}
      >
        <p style={{ margin: "0 0 8px 0" }}>{`${payload[0].name}: ${payload[0].value.toFixed(1)}%`}</p>
        <p style={{ margin: 0, fontSize: 10 }}>{getIntroOfPage(payload[0].name)}</p>
      </div>
    );
  }
  return null;
};

function ChargingSummaryPieChart({
  total,
  unit,
  title,
  height = 400,
  width = 400,
  data,
  accentColor = 'primary'
}: ChargingSummaryPieChartProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  // Define colors based on MUI theme
  const chartColors = [
    theme.palette[accentColor]?.main || theme.palette.primary.main,
    theme.palette.grey[300]
  ];

  if (!data || data.length === 0) {
    return (
      <DashboardCard
        title={title}
        icon={<PieChartIcon />}
        accentColor={accentColor}
      >
        <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          No data available
        </Box>
      </DashboardCard>
    );
  }

  // Transform the data to use percentage as value
  const chartData: ChartDataItem[] = data.map(item => ({
    name: item.name,
    value: item.percentage
  }));

  return (
    <DashboardCard
      title={title}
      icon={<PieChartIcon />}
      accentColor={accentColor}
    >
      <Box sx={{ width: '100%', height: 400, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              innerRadius={70}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value, entry, index) => (
                <span style={{ color: theme.palette.text.primary }}>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            color: theme.palette.text.primary 
          }}>{total}</div>
          <div style={{ 
            fontSize: '0.875rem', 
            color: theme.palette.text.secondary 
          }}>{unit}</div>
        </Box>
      </Box>
    </DashboardCard>
  );
}

export default ChargingSummaryPieChart;
